import type { OdinDataRetrievalOptions } from '@myosh/odin-components';
import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { v4 } from 'uuid';
import { apiJobTypes, type BackgroundJobsItem } from '../../@types/background-jobs';
import type { DynamicFormSettings } from '../../@types/forms';
import type {
  CloneRecordsApiProps,
  ExportFileFormat,
  ExportRecordFieldApiProps,
  RecordResult,
} from '../../@types/records';
import type { RootState } from '../store';

export type ExportSingleRecordBackgroundJobApiProps = {
  formName: string;
  format: ExportFileFormat;
  recordId: string;
  appendRecordlinksAsSeparatePages?: boolean; // PDF format only
  appendSignaturePage?: boolean; // PDF format only
  sectionIds?: string; // PDF format only
  includeHierarchy?: boolean; // PDF format only
};

export type ExportRecordsBackgroundJobApiProps = {
  format: string;
  formName: string;
  columns?: string;
  gridOptions?: OdinDataRetrievalOptions;
  moduleId?: number;
  formId?: string;
  allForms?: boolean; // flag for all forms export
  globalHierarchyFilters?: string;
  sortDirective?: string;
};

export type ExportFullRecordsBackgroundJobApiProps = Omit<
  ExportRecordsBackgroundJobApiProps,
  'allForms' | 'moduleId'
> & {
  exportFullRecordData: boolean;
  includeLinkedRecords?: boolean;
  includeAttachments?: boolean;
};

export type AnalyzeRecordsBackgroundJobApiProps = ExportRecordsBackgroundJobApiProps & {
  viewId: string;
  extraPrompt?: string; // additional prompt for AI analysis
};

// we enrich the details with api data once the polling starts
export type FileBackgroundJobDetails = BackgroundJobsItem;

export type RecordBackgroundJobDetails = BackgroundJobsItem & {
  text: string;
  hasMultipleRecords: boolean;
  record?: RecordResult;
};

export type ImportFormBackgroundJobDetails = {
  moduleFormId: number;
  moduleIcon: string;
  templateFormId: number;
  backgroundJobId: number; // the job id from the server
  id?: string; // the v4 job id in the client
  formConfig?: DynamicFormSettings;
  fileName?: string; // the name of the file that was uploaded
};

export type SaveRecordBackgroundJobProps = {
  jobId: string;
  record?: Partial<RecordResult>;
  details: Record<string, unknown>;
};

export type ImportAttachmentsBackgroundJobProps = {
  jobId: number;
  prefix?: 'RECORD_ID' | 'RECORD_SOURCE_ID';
};

export const JobStatus = {
  NotStarted: 0,
  InProgress: 1,
  Success: 2,
  Failed: 3,
} as const;
export type JobStatus = (typeof JobStatus)[keyof typeof JobStatus];

export const JobType = {
  ExportSingleRecord: 0,
  ExportRecords: 1,
  ExportFullRecords: 2,
  ExportRecordField: 3,
  CloneRecords: 4,
  AnalyzeRecords: 5,
  ImportForm: 6,
  SaveRecord: 7,
  UpdateRecord: 8,
  ImportAttachments: 9,
} as const;
export type JobType = (typeof JobType)[keyof typeof JobType];

// List of Job types we do not show in the UI panel
export const jobTypesExcludedFromPanel: Set<JobType> = new Set([JobType.SaveRecord, JobType.UpdateRecord]);

// Map of API job types to client JobType enum (used for hydrating jobs from API)
export const apiTypeToJobType: Record<number, JobType> = {
  [apiJobTypes.RECORD_EXPORT]: JobType.ExportSingleRecord,
  [apiJobTypes.RECORDLIST_EXPORT]: JobType.ExportRecords,
  [apiJobTypes.FULL_RECORDS_EXPORT]: JobType.ExportFullRecords,
  [apiJobTypes.RECORD_FIELD_EXPORT]: JobType.ExportRecordField,
  [apiJobTypes.CLONE_RECORD]: JobType.CloneRecords,
  [apiJobTypes.ATTACHMENT_IMPORT]: JobType.ImportAttachments,
};

const apiStatusToJobStatus: Record<string, JobStatus> = {
  FINISHED: JobStatus.Success,
  FAILED: JobStatus.Failed,
  STAGED: JobStatus.InProgress, // staged means it's started, but not yet running
  RUNNING: JobStatus.InProgress,
};

export interface BackgroundJobDetails<T = Record<string, unknown>, U = Record<string, unknown>> {
  id: string;
  status: JobStatus;
  type: JobType;
  completedDate?: string;
  apiProps: T;
  details?: U;
  deleted?: boolean; // flag to indicate if the job was deleted
}

interface BackgroundJobState {
  submittedJobs: Array<BackgroundJobDetails>;
  isPanelOpen: boolean;
}

const initialState: BackgroundJobState = {
  submittedJobs: [],
  isPanelOpen: false,
};

const backgroundJobSlice = createSlice({
  name: 'backgroundJobState',
  initialState,
  reducers: {
    submitExportSingleRecordJob: (state, action: PayloadAction<ExportSingleRecordBackgroundJobApiProps>) => {
      state.submittedJobs.unshift({
        id: v4(),
        status: JobStatus.NotStarted,
        type: JobType.ExportSingleRecord,
        apiProps: action.payload,
      });
    },
    submitExportRecordsJob: (state, action: PayloadAction<ExportRecordsBackgroundJobApiProps>) => {
      state.submittedJobs.unshift({
        id: v4(),
        status: JobStatus.NotStarted,
        type: JobType.ExportRecords,
        apiProps: action.payload,
      });
    },
    submitExportFullRecords: (state, action: PayloadAction<ExportFullRecordsBackgroundJobApiProps>) => {
      state.submittedJobs.unshift({
        id: v4(),
        status: JobStatus.NotStarted,
        type: JobType.ExportFullRecords,
        apiProps: action.payload,
      });
    },
    submitExportRecordFieldJob: (state, action: PayloadAction<ExportRecordFieldApiProps>) => {
      state.submittedJobs.unshift({
        id: v4(),
        status: JobStatus.NotStarted,
        type: JobType.ExportRecordField,
        apiProps: action.payload,
      });
    },
    submitCloneRecordsJob: (
      state,
      action: PayloadAction<CloneRecordsApiProps & { details: Partial<RecordBackgroundJobDetails> }>
    ) => {
      const { details, ...apiProps } = action.payload;

      state.submittedJobs.unshift({
        id: v4(),
        status: JobStatus.NotStarted,
        type: JobType.CloneRecords,
        apiProps: apiProps,
        details: details,
      });
    },
    submitAnalyzeRecordsJob: (state, action: PayloadAction<AnalyzeRecordsBackgroundJobApiProps>) => {
      state.submittedJobs.unshift({
        id: v4(),
        status: JobStatus.NotStarted,
        type: JobType.AnalyzeRecords,
        apiProps: action.payload,
      });
    },
    submitImportFormJob: (state, action: PayloadAction<ImportFormBackgroundJobDetails>) => {
      state.submittedJobs.unshift({
        id: v4(),
        status: JobStatus.NotStarted,
        type: JobType.ImportForm,
        apiProps: {}, // no api props for import form
        details: action.payload,
      });
    },
    submitSaveRecordJob: (state, action: PayloadAction<SaveRecordBackgroundJobProps>) => {
      state.submittedJobs.unshift({
        id: action.payload.jobId,
        status: JobStatus.NotStarted,
        type: JobType.SaveRecord,
        apiProps: action.payload.record ?? {},
        details: action.payload.details,
      });
    },
    submitUpdateRecordJob: (state, action: PayloadAction<SaveRecordBackgroundJobProps>) => {
      state.submittedJobs.unshift({
        id: action.payload.jobId,
        status: JobStatus.NotStarted,
        type: JobType.UpdateRecord,
        apiProps: action.payload.record ?? {},
        details: action.payload.details,
      });
    },
    submitImportAttachmentsJob: (state, action: PayloadAction<ImportAttachmentsBackgroundJobProps>) => {
      state.submittedJobs.unshift({
        id: v4(),
        status: JobStatus.NotStarted,
        type: JobType.ImportAttachments,
        apiProps: action.payload,
      });
    },
    setBackgroundJobDetails: (state, action: PayloadAction<Partial<BackgroundJobDetails>>) => {
      const jobIndex = state.submittedJobs.findIndex((job) => job.id === action.payload.id);
      if (jobIndex !== -1) {
        state.submittedJobs[jobIndex] = {
          ...state.submittedJobs[jobIndex],
          ...action.payload,
          details: {
            ...state.submittedJobs[jobIndex].details,
            ...action.payload.details,
          },
        };
      }
    },
    setIsPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.isPanelOpen = action.payload;
    },
    removeBackgroundJob: (state, action: PayloadAction<string>) => {
      const jobIndex = state.submittedJobs.findIndex((job) => job.id === action.payload);
      if (jobIndex !== -1) {
        // instead of removing the job, we mark it as deleted
        state.submittedJobs[jobIndex] = {
          ...state.submittedJobs[jobIndex],
          deleted: true,
        };
      }
    },
    revertRemoveBackgroundJob: (state, action: PayloadAction<string>) => {
      const jobIndex = state.submittedJobs.findIndex((job) => job.id === action.payload);
      if (jobIndex !== -1) {
        state.submittedJobs[jobIndex] = {
          ...state.submittedJobs[jobIndex],
          deleted: false,
        };
      }
    },
    hydrateJobs: (state, action: PayloadAction<BackgroundJobsItem[]>) => {
      action.payload.forEach((apiJob) => {
        const exists = state.submittedJobs.some(
          (job) => (job.details as Record<string, unknown>)?.backgroundJobId === apiJob.id
        );

        if (exists) {
          return; // if job already exists (by backgroundJobId in details)
        }

        const status = apiStatusToJobStatus[apiJob.status];
        const jobType = apiTypeToJobType[apiJob.type];

        if (status === undefined || jobType === undefined) {
          return; // unknown status or type, skip this job
        }

        state.submittedJobs.unshift({
          id: `hydrated-${apiJob.id}`,
          status,
          type: jobType,
          apiProps: {
            // job has already been started previously
          },
          details: {
            ...apiJob,
            backgroundJobId: apiJob.id,
            isHydrated: true,
          },
        });
      });
    },
  },
});

export const {
  submitExportSingleRecordJob,
  submitExportRecordsJob,
  submitExportFullRecords,
  submitExportRecordFieldJob,
  submitCloneRecordsJob,
  submitAnalyzeRecordsJob,
  submitImportFormJob,
  submitSaveRecordJob,
  submitUpdateRecordJob,
  submitImportAttachmentsJob,
  setBackgroundJobDetails,
  setIsPanelOpen,
  removeBackgroundJob,
  revertRemoveBackgroundJob,
  hydrateJobs,
} = backgroundJobSlice.actions;

export const selectIsPanelOpen = (state: RootState) => state.backgroundJobState.isPanelOpen;

const selectAllSubmittedJobs = (state: RootState) => state.backgroundJobState.submittedJobs;

export const selectSubmittedJobs = createSelector([selectAllSubmittedJobs], (submittedJobs) => {
  return submittedJobs.filter((job) => Boolean(job.deleted) === false);
});

export const selectNotStartedJobs = createSelector([selectSubmittedJobs], (submittedJobs) => {
  return submittedJobs.filter((job) => job.status === JobStatus.NotStarted);
});

export const selectJobsForBackgroundJobPanel = createSelector([selectSubmittedJobs], (submittedJobs) => {
  // Exclude SaveRecord and UpdateRecord jobs from the background job panel
  return submittedJobs.filter((job) => !jobTypesExcludedFromPanel.has(job.type));
});

export const selectHydratedJobs = createSelector([selectSubmittedJobs], (submittedJobs) => {
  return submittedJobs.filter(
    (job) => job.status === JobStatus.InProgress && (job.details as Record<string, unknown>)?.isHydrated === true
  );
});

export default backgroundJobSlice.reducer;
